/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	generateSimplestList,
	readOnlyAttribute,
	recalcRequiredLocalAttribute,
	SimpleAttributeList,
	simpleLocalAttribute,
} from './SimpleAttribute';
import { DefaultSplitDataRenderType, SplitData } from './SplitData';
import { IPlanAccessFields, PlanAccessLevel } from './plan-access';
import { PLAN_ARCHIVED_PREFIX } from '@touchplan/strings';

export { DefaultSplitDataRenderType };

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface Milestone {
	daysBehind?: number;
	scheduleDate?: string;
	scheduleX?: number;
	targetX?: number;
	targetDate?: string;
	activityName?: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface PromisePeriodStack {
	endDate?: string;
	startDate?: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface PullColumn {
	finishX?: number;
	pullColumn?: number;
	startX?: number;
	tminusFinish?: number;
	plannedStart?: string;
	plannedFinish?: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface Snapshot {
	initiatedBy?: string;
	prettyId?: string;
	requestName?: string;
	takenAt?: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface PlanFields {
	planHasActiveSpace?: boolean;
	name?: string;
	activeLineX?: number;
	activeLineDate?: string;
	activeFinishDate?: string;
	activeFinishX?: number;
	activeStartDate?: string;
	activeStartX?: number;
	activatePullColumn?: number;
	blockedCount?: number;
	calDayWidthPixels?: number;
	calMinWidthPixels?: number;
	calTicketHeightPixels?: number;
	createdAt?: string;
	customPlanLabel?: string;
	cycleCount?: number;
	earliestLastPlannedStart?: string;
	earliestPlannedStart?: string;
	fbdbTime?: string;
	fbdbTimeX?: number;
	fbdbUpdateDaemon?: string;
	fbdbUpdateMillis?: number;
	fbdbUpdateRuntime?: string;
	growActiveSpace?: number;
	planAccess?: IPlanAccessFields;
	latestActualFinish?: string;
	latestLastPlannedFinish?: string;
	latestPlannedFinish?: string;
	latestSnapshotId?: string;
	latestSnapshotPrettyId?: string;
	maxDate?: string;
	maxX?: number;
	maxY?: number;
	milestone?: Milestone;
	minDate?: string;
	minX?: number;
	minY?: number;
	outOfOrderCount?: number;
	outlierSpacingPixels?: number;
	planId?: string;
	promisePeriodPosition?: string;
	promisePeriodStack?: { [key: string]: PromisePeriodStack };
	pullColumns?: PullColumn[];
	pullFinishX?: number;
	pullTicketHeightPixels?: number;
	pullTicketWidth?: number;
	snapshot?: Snapshot;
	targetStartDate?: string;
	targetFinishDate?: string;
	ticketCount?: number;
	wbsCode?: number;
	deletedAt?: string;
	startingWeek?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
}

export class Plan extends SplitData<PlanFields, DefaultSplitDataRenderType> {
	protected useInPlace: boolean = true;
	protected attributeList: SimpleAttributeList = Object.assign(
		generateSimplestList([
			//full read/write
			'name',
			'activeLineX',
			'activeLineDate',
			'activeFinishDate',
			'activeFinishX',
			'activeStartDate',
			'activeStartX',
			`activatePullColumn`,
			'blockedCount',
			'createdAt',
			'cycleCount',
			'deletedAt',
			'earliestLastPlannedStart',
			'earliestPlannedStart',
			'growActiveSpace',
			'maxDate',
			'maxX',
			'maxY',
			'milestone',
			'minDate',
			'minX',
			'minY',
			'outlierSpacingPixels',
			'planId',
			'promisePeriodPosition',
			'promisePeriodStack',
			'pullColumns',
			'pullFinishX',
			'targetStartDate',
			'targetFinishDate',
			'ticketCount',
			'wbsCode',
			'pullTicketWidth',
			'startingWeek',
		]),
		{
			//from fbdb only
			calDayWidthPixels: readOnlyAttribute,
			calMinWidthPixels: readOnlyAttribute,
			calTicketHeightPixels: readOnlyAttribute,
			fbdbTime: readOnlyAttribute,
			fbdbTimeX: readOnlyAttribute,
			fbdbUpdateDaemon: readOnlyAttribute,
			fbdbUpdateMillis: readOnlyAttribute,
			fbdbUpdateRuntime: readOnlyAttribute,
			latestActualFinish: readOnlyAttribute,
			latestLastPlannedFinish: readOnlyAttribute,
			latestPlannedFinish: readOnlyAttribute,
			latestSnapshotId: readOnlyAttribute,
			latestSnapshotPrettyId: readOnlyAttribute,
			outOfOrderCount: readOnlyAttribute,
			pullTicketHeightPixels: readOnlyAttribute,
			pullTicketWidthPixels: readOnlyAttribute,
			snapshot: readOnlyAttribute,
			//local only
			customPlanLabel: simpleLocalAttribute,
			planHasActiveSpace: recalcRequiredLocalAttribute,
			planAccess: simpleLocalAttribute,
		}
	);

	public constructor(id: string) {
		super(id, DefaultSplitDataRenderType.database);
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	private recalcLocal(renderType?: DefaultSplitDataRenderType): void {
		if (!renderType) {
			renderType = this.currentRenderType;
		}
		const view = this[renderType];
		view.planHasActiveSpace = view.activeLineX !== undefined;
		view.customCSVDropdownLabel =
			view.planAccess?.accessLevel === PlanAccessLevel.archived
				? `${PLAN_ARCHIVED_PREFIX} ${view.name}`
				: view.name;
	}

	// override
	public databaseUpdate(data: object): void {
		const res = super.databaseUpdate(data);
		this.recalcLocal(DefaultSplitDataRenderType.database);
		return res;
	}

	// override
	public localUpdate(data: object, renderType?: DefaultSplitDataRenderType): void {
		const res = super.localUpdate(data, renderType);
		this.recalcLocal(renderType);
		return res;
	}
}
